<template>
  <slot
    name="default"
    :loading="jobSharesLoading"
    :contacts="selectedContacts"
    :trigger="toggleContactsModal"
  >
    <WdsStack direction-row align-center gap-md>
      <WdsSpinner v-if="jobSharesLoading" indeterminate />

      <WdsStack direction-row align-center gap-sm>
        <WdsAvatarGroup
          v-if="selectedContacts.length > 0"
          sm
          :avatars="selectedContacts"
          :max-avatars="3"
          data-test="collaborators-avatars"
        />

        <WdsButton
          secondary
          :disabled="jobSharesLoading || isJobPublished === false"
          data-test="collaborators-management-button"
          @click="toggleContactsModal(!showContactsModal)"
        >
          {{ $t('Add/Remove') }}
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </slot>

  <teleport to="body">
    <WdsModal
      lg
      :show="showContactsModal"
      :title="$t('Select recruiters')"
      data-test="collaborators-invite-modal"
      @cancel="toggleContactsModal(false)"
    >
      <RecruitersSelection
        :job-id="jobId"
        :pre-selected-recruiters="state.contacts"
        @updated="handleContactsUpdated"
        @cancel="toggleContactsModal(false)"
      />
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, provide, readonly } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsStack, WdsAvatarGroup, WdsButton, WdsModal, WdsSpinner } from '@worksome/wds-components'

import { useFragment } from '@/gql'
import { $t } from '@/composables/translation'
import AccountType from '@/utils/AccountType'

import {
  ManageWorkersInterface,
  manageWorkersKey,
} from '@/components/Contacts/CompanyRecruiter/injectionKeys'
import { Recruiter } from '@/components/Job/hiring-types'
import { formatRecruiterAsAvatar } from '@/components/Job/functions'
import RecruitersSelection from '@/components/Job/RecruitersSelection.vue'
import { GetJobSharesQuery } from '@/components/Job/Share/graphql/queries'
import { JobFormShareableRecruiterFragment } from '@/components/Job/Share/graphql/fragments'

const props = defineProps({
  jobId: {
    type: String,
    required: true,
  },

  isPublished: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['contacts-updated'])

const state = ref<{
  contacts: Recruiter[]
}>({
  contacts: [],
})
const manageWorkers = ref(false)

provide<ManageWorkersInterface>(manageWorkersKey, {
  manageWorkers: readonly(manageWorkers),
})

const showContactsModal = ref<boolean>(false)

const isJobPublished = computed(() => props.isPublished)

const selectedContacts = computed(() => state.value.contacts.map(formatRecruiterAsAvatar))

const {
  onResult: onJobResult,
  loading: jobSharesLoading,
  refetch: refetchJobShares,
} = useQuery(
  GetJobSharesQuery,
  {
    jobId: props.jobId,
  },
  { clientId: 'publicApi' },
)

onJobResult((queryJobResult) => {
  if (!queryJobResult.loading && queryJobResult.data && queryJobResult.data.job) {
    const { job } = queryJobResult.data

    state.value.contacts = []
    manageWorkers.value = job.company?.settings?.staffingAgencyManagesWorker ?? false
    job.shares.data.forEach((share: any) => {
      if (share.account.__typename && AccountType.isRecruiter(share.account.__typename)) {
        const recruiter = useFragment(JobFormShareableRecruiterFragment, share!.account!)

        state.value.contacts.push({
          id: share.id,
          recruiterId: recruiter.id,
          avatar: recruiter.avatar,
          name: recruiter.name,
        } as Recruiter)
      }
    })
  }
})

const toggleContactsModal = (show: boolean) => {
  showContactsModal.value = show
}

const handleContactsUpdated = async () => {
  await refetchJobShares()

  emit('contacts-updated')
}
</script>
